import { useState, useContext, useEffect } from 'react';
import classnames from 'classnames';
import { ReactRelayContext, createFragmentContainer, graphql } from 'react-relay/legacy';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl, defineMessages } from 'dibs-react-intl';
import PropTypes from 'prop-types';
import get from 'lodash.get';
import serverVars from 'server-vars';
import { mobile } from 'dibs-client-check';
import { getBuyerId } from 'dibs-cookie-jar';
import { Input } from 'dibs-elements/exports/Input';
import { Button } from 'dibs-elements/exports/Button';
import { Spinner } from 'dibs-elements/exports/Spinner';
import ArrowRight from 'dibs-icons/exports/ArrowRight';
import { trackRegistrationEntry } from '../../../authModal/authentication/authTracking';
import authCopy from '../../../authModal/authentication/authCopy';
import { PrivacyPolicyLink, UserAgreementLink } from '../../../authModal/shared/Legal';
import { subscribeUserMutation } from './subscribeUserMutation';
import { registrationOrLoginHandler } from '../../../authModal/authentication/registrationOrLoginHandler';
import { SAVE_SUBSCRIPTION_PREFERENCES_FLOW } from '../../../authModal/authentication/authFlowConstants';

import styles from './styles.scss';

const GA_LABEL = 'footer navigation';

const messages = defineMessages({
    emailPlaceholder: {
        id: 'dbl.footer.FooterSubscribe.emailPlaceholder',
        defaultMessage: "Yes, here's my email",
    },
    tabletEmailPlaceholder: {
        id: 'dbl.footer.FooterSubscribe.tabletEmailPlaceholder',
        defaultMessage: 'Email',
    },
});

export const FooterSubscribeComponent = ({ isMobile, viewer }) => {
    const intl = useIntl();
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showSubscribeComponent, setShowSubscribeComponent] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [hasFiredEntryTracking, setHasFiredEntryTracking] = useState(false);
    const { environment } = useContext(ReactRelayContext) || {};
    const isTablet = serverVars.get('settings.isTablet');
    const isIpad = mobile.iPad();

    function registerAndSubscribe(e) {
        e.preventDefault();

        registrationOrLoginHandler({
            email,
            errorHandler: setErrorMessage,
            loadingHandler: setIsLoading,
            userRegistrationHandler: () => setShowConfirmation(true),
            userLoginHandler: res => {
                if (res.userId) {
                    subscribeUserMutation(environment, res.userId)
                        .then(() => {
                            setShowConfirmation(true);
                        })
                        .catch(() => setErrorMessage(authCopy.errors.support));
                } else {
                    setIsLoading(false);
                }
            },
            environment,
            gaLabel: GA_LABEL,
            flow: SAVE_SUBSCRIPTION_PREFERENCES_FLOW,
        });
    }

    function trackEntry() {
        if (!hasFiredEntryTracking) {
            trackRegistrationEntry({ label: GA_LABEL });
            setHasFiredEntryTracking(true);
        }
    }

    useEffect(() => {
        if (get(viewer, 'user.serviceId') && !isLoading) {
            setShowSubscribeComponent(false);
        }
    }, [viewer, isLoading]);
    useEffect(() => {
        setShowSubscribeComponent(!getBuyerId(document.cookie));
    }, []);

    return (
        showSubscribeComponent && (
            <div className={styles.footerSubscribe}>
                {showConfirmation ? (
                    <FormattedMessage
                        id="dbl.footer.FooterSubscribe.confirmation"
                        defaultMessage="Thanks for subscribing!"
                    />
                ) : (
                    <>
                        <form className={styles.footerForm} onSubmit={registerAndSubscribe}>
                            <p
                                className={classnames(
                                    styles.subscribeText,
                                    styles.subscribeDescription
                                )}
                            >
                                <FormattedMessage
                                    id="dbl.footer.FooterSubscribe.subheader"
                                    defaultMessage="Sign up for weekly emails with new listings, sales and more!"
                                />
                            </p>
                            <div className={styles.inputWrapper}>
                                <Input
                                    dataTn="footer-subscribe-input"
                                    className={styles.input}
                                    size={isMobile ? 'medium' : 'small'}
                                    horizontalSpacing="small"
                                    placeholder={intl.formatMessage(
                                        isTablet || isIpad
                                            ? messages.tabletEmailPlaceholder
                                            : messages.emailPlaceholder
                                    )}
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    onFocus={trackEntry}
                                    errorMessage={
                                        errorMessage ? (
                                            <div className={styles.error}>{errorMessage}</div>
                                        ) : null
                                    }
                                    type="email"
                                />
                            </div>
                            <Button
                                type="secondary"
                                className={styles.button}
                                dataTn="footer-subscribe-submit"
                                size={isMobile ? 'medium' : 'small'}
                            >
                                {isLoading ? (
                                    <Spinner black containerClass={styles.spinner} size="tiny" />
                                ) : (
                                    <ArrowRight className={styles.arrow} />
                                )}
                            </Button>
                        </form>
                        <div className={styles.subscribeText}>
                            <FormattedMessage
                                id="dbl.footer.FooterSubscribe.gdprText"
                                defaultMessage="By entering your email, you agree to our {userAgreementLink} and {privacyPolicyLink}"
                                values={{
                                    userAgreementLink: (
                                        <UserAgreementLink className={styles.subscribeLink} />
                                    ),
                                    privacyPolicyLink: (
                                        <PrivacyPolicyLink className={styles.subscribeLink} />
                                    ),
                                }}
                            />
                        </div>
                    </>
                )}
            </div>
        )
    );
};

FooterSubscribeComponent.propTypes = {
    isMobile: PropTypes.bool,
    viewer: PropTypes.object,
};

const mapStateToProps = ({ header }) => ({
    isMobile: header.isMobile,
});

export default createFragmentContainer(connect(mapStateToProps)(FooterSubscribeComponent), {
    viewer: graphql`
        fragment FooterSubscribe_viewer on Viewer {
            user(userId: $userId) @include(if: $hasUserId) {
                serviceId
            }
        }
    `,
});
